// Проекция стора в кеш и восстановление из кеша (SPEC §4.6) — инвариант 3 (офлайн-чтение).
// Кешируем ВЫБОРОЧНУЮ проекцию (последние cacheWindowPerChat committed-сообщений на чат, список
// чатов, last-seq, черновики, справочник users) — не весь стор. Оптимистичные/typing/presence
// эфемерны и в кеш НЕ уходят. При восстановлении сервер всё равно побеждает (sync.delta + listChats).
import { ensureChatMessages, insertSeqOrdered, } from '../state.js';
import { resortChats } from '../sort.js';
/**
 * Освежает derived-поля вложений ИСТОРИЧЕСКИХ снапшотов под текущий канон медиа-URL (2026-07-04):
 * раньше thumbnailUrl указывал на /blob (оригинал) — плитки ленты качали мегабайтные оригиналы,
 * и замороженный в снапшоте URL не давал клиенту подхватить новый /thumb (512px webp) без полной
 * перезагрузки истории. Сервер — источник истины URL-формы; здесь только миграция старой записи:
 * image/video → тот же путь с /thumb; audio/file превью не имеют (плашка) → поле снимается.
 * Мутируем безопасно: объекты только что распарсены из IndexedDB, в стор ещё не вставлены.
 */
function normalizeAttachmentUrls(msg) {
    if (msg.attachments === undefined)
        return;
    for (const att of msg.attachments) {
        if (att.thumbnailUrl !== undefined && att.thumbnailUrl.endsWith('/blob')) {
            if (att.kind === 'image' || att.kind === 'video') {
                att.thumbnailUrl = `${att.thumbnailUrl.slice(0, -'/blob'.length)}/thumb`;
            }
            else {
                delete att.thumbnailUrl;
            }
        }
    }
}
/** Собрать выборочную проекцию состояния в снапшот для персиста (SPEC §4.6 persist + вытеснение). */
export function projectSnapshot(state, policy, now) {
    // LRU чатов по lastMessage.createdAt (свежие активные) — берём cacheChatLimit штук.
    const chatIds = Object.keys(state.chats.byId).slice();
    chatIds.sort((a, b) => activityMs(state, b) - activityMs(state, a));
    const keep = chatIds.slice(0, Math.max(0, policy.cacheChatLimit));
    const chats = keep.map((id) => state.chats.byId[id]).filter(Boolean);
    const messagesByChat = {};
    const cursors = {};
    const drafts = {};
    const users = {};
    for (const id of keep) {
        const cm = state.messages.byChat[id];
        if (cm) {
            // окно: последние cacheWindowPerChat committed по seq (тумбстоуны кешируются как тумбстоуны)
            const tailSeqs = cm.orderedSeq.slice(-Math.max(0, policy.cacheWindowPerChat));
            const msgs = [];
            for (const seq of tailSeqs) {
                const m = cm.bySeq[seq];
                if (m)
                    msgs.push(m);
            }
            messagesByChat[id] = msgs;
            cursors[id] = cm.maxSeq;
        }
        const draft = state.drafts[id];
        if (draft !== undefined && draft !== '')
            drafts[id] = draft;
    }
    // users, упомянутые в кешируемых сообщениях/чатах — для офлайн-рендера отправителей
    for (const id of keep) {
        const chat = state.chats.byId[id];
        if (chat)
            for (const uid of chat.memberIds)
                putUser(state, users, uid);
        for (const m of messagesByChat[id] ?? [])
            putUser(state, users, m.senderId);
    }
    return { chats, messagesByChat, cursors, drafts, users, savedAt: now };
}
function putUser(state, out, uid) {
    const u = state.users[uid];
    if (u && u.name !== '')
        out[uid] = u;
}
function activityMs(state, id) {
    const chat = state.chats.byId[id];
    if (!chat)
        return 0;
    const ts = chat.lastMessage?.createdAt ?? chat.createdAt;
    const ms = Date.parse(ts);
    return Number.isNaN(ms) ? 0 : ms;
}
/**
 * Развернуть снапшот в нормализованную форму ДО первой отрисовки (SPEC §4.6 hydrate).
 * Наполняет chats/messages/users/drafts/cursors. Курсоры из снапшота возвращаются вызывающему,
 * чтобы сразу отдать транспорту setCursors (sync доберёт только дельту).
 */
export function restoreSnapshot(state, snap, ctx) {
    for (const chat of snap.chats) {
        state.chats.byId[chat.id] = chat;
        state.unread[chat.id] = chat.myState.unreadCount;
        if (chat.myState.draft !== undefined)
            state.drafts[chat.id] = chat.myState.draft;
    }
    for (const [uid, user] of Object.entries(snap.users)) {
        state.users[uid] = user;
    }
    for (const [chatId, msgs] of Object.entries(snap.messagesByChat)) {
        const cid = chatId;
        const cm = ensureChatMessages(state, cid);
        for (const msg of msgs) {
            normalizeAttachmentUrls(msg);
            cm.bySeq[msg.seq] = msg;
            insertSeqOrdered(cm.orderedSeq, msg.seq);
            state.messageIndex[msg.id] = { chatId: cid, seq: msg.seq };
            if (msg.seq > cm.maxSeq)
                cm.maxSeq = msg.seq;
        }
        // ниже минимального кешированного seq есть непрогруженная история
        cm.gapBelow = true;
    }
    for (const [chatId, draft] of Object.entries(snap.drafts)) {
        state.drafts[chatId] = draft;
    }
    const cursors = [];
    for (const [chatId, afterSeq] of Object.entries(snap.cursors)) {
        const cid = chatId;
        const cm = state.messages.byChat[cid];
        if (cm && afterSeq > cm.maxSeq)
            cm.maxSeq = afterSeq;
        cursors.push({ chatId: cid, afterSeq });
    }
    resortChats(state);
    void ctx; // ctx зарезервирован для будущей диагностики восстановления; восстановление само событий не эмитит
    return cursors;
}
//# sourceMappingURL=snapshot.js.map